import { useMutation, useQuery } from 'react-query';
import pecaAiBasketServices from './pecaAiBasket.services';
import notificationError from '@/utils/functions/notificationError';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { notification } from 'antd';
import {
  PecaAiBasketCombined,
  PecaAiBasketDetailRequest,
  PecaAiBasketEditItemRequest,
  PecaAiBasketOrdersRequest,
  PecaAiBasketRemoveItemRequest,
  PecaAiBasketShippingsRequest,
  PecaAiBasketVouncherRequest,
} from './pecaAiBasket.types';
import {
  useWorkOrderComplementBindWithPecaAi,
  useWorkorderComplementSyncWithPecaAi,
  WORKORDER_COMPLEMENT_DETAIL_KEY,
} from '../../workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { WORKORDER_ITEMS_QUERY_KEY } from '../../workorder/WorkOrdersItems/WorkOrdersItems.hooks';

export const PECAAI_BASKET_LIST = 'peca-ai/basket/list';
export const PECAAI_BASKET_DETAIL = 'peca-ai/basket/detail';
export const PECAAI_BASKET_SHIPPINGS = 'peca-ai/basket/shippings';

export function usePecaAiBasketDetail(
  params: PecaAiBasketDetailRequest,
  enabled = true,
) {
  const safe = <T>(p: PromiseSettledResult<T>) =>
    p.status === 'fulfilled' ? p.value : null;

  return useQuery<PecaAiBasketCombined>(
    [PECAAI_BASKET_DETAIL, { ...params, enabled }],
    async () => {
      const [detail, summary] = await Promise.allSettled([
        pecaAiBasketServices.detailBasket(params),
        pecaAiBasketServices.basketSummary(params),
      ]);

      return {
        detail: safe(detail),
        summary: safe(summary),
      };
    },
    {
      enabled: enabled && !!params.basketId && !!params.document,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function usePecaAiBasketCreate(params: {
  workOrderId?: number;
  complementId?: number;
}) {
  const queryChanel = useQueryChanel();
  const bindBasketInWorkorder = useWorkOrderComplementBindWithPecaAi();
  const syncBasketInWorkorder = useWorkorderComplementSyncWithPecaAi();

  return useMutation(pecaAiBasketServices.createBasket, {
    async onSuccess() {
      if (params.complementId && params.workOrderId) {
        await bindBasketInWorkorder.mutateAsync({
          description: '',
          products: [],
          workOrderId: params.workOrderId,
          complementId: params.complementId,
        });
        await syncBasketInWorkorder.mutateAsync({
          complementId: params.complementId,
          workOrderId: params.workOrderId,
        });
      }

      queryChanel({
        invalidateQueries: [
          [PECAAI_BASKET_LIST],
          [PECAAI_BASKET_SHIPPINGS],
          ['workOrders'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY],
          [WORKORDER_ITEMS_QUERY_KEY],
        ],
        removeQueries: [[WORKORDER_COMPLEMENT_DETAIL_KEY]],
      });

      notification.success({
        message: 'A cesta foi criada e o orçamento foi atualizado com sucesso!',
      });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function usePecaAiBasketUpdate(params: {
  workOrderId?: number;
  complementId?: number;
}) {
  const queryChanel = useQueryChanel();
  const syncBasketInWorkorder = useWorkorderComplementSyncWithPecaAi();

  return useMutation(pecaAiBasketServices.updateBasket, {
    async onSuccess() {
      if (params.complementId && params.workOrderId) {
        await syncBasketInWorkorder.mutateAsync({
          complementId: params.complementId,
          workOrderId: params.workOrderId,
        });
      }

      queryChanel({
        invalidateQueries: [
          [PECAAI_BASKET_LIST],
          [PECAAI_BASKET_DETAIL],
          [PECAAI_BASKET_SHIPPINGS],
          ['workOrders'],
          [WORKORDER_COMPLEMENT_DETAIL_KEY],
          [WORKORDER_ITEMS_QUERY_KEY],
        ],
        removeQueries: [[WORKORDER_COMPLEMENT_DETAIL_KEY]],
      });

      notification.success({
        message: 'A cesta e o orçamento foram atualizados com sucesso!',
      });
    },
    onError(err) {
      notificationError(err);
    },
  });
}

export function usePecaAiBasketShippings(
  params: PecaAiBasketShippingsRequest,
  enabled = true,
) {
  return useQuery(
    [PECAAI_BASKET_SHIPPINGS, { ...params }],
    () => pecaAiBasketServices.shippingsBasket(params),
    {
      enabled: enabled && !!params.basketId && !!params.buyer,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function usePecaAiBasketVouncher(
  params: Omit<PecaAiBasketVouncherRequest, 'voucherCode'>,
) {
  const queryChanel = useQueryChanel();

  return useMutation(
    (voucherCode: string) =>
      pecaAiBasketServices.vouncherBasket({ ...params, voucherCode }),
    {
      onSuccess() {
        queryChanel({ invalidateQueries: [[PECAAI_BASKET_DETAIL]] });
        notification.success({
          message: 'Cupom aplicado com sucesso!',
        });
      },
      onError() {
        notification.error({
          message: 'Cupom informado inválido!',
        });
      },
    },
  );
}

export function usePecaAiBasketOrders(params: PecaAiBasketOrdersRequest) {
  const queryChanel = useQueryChanel();

  return useMutation(() => pecaAiBasketServices.ordersBasket(params), {
    onSuccess() {
      queryChanel({ invalidateQueries: [[PECAAI_BASKET_DETAIL]] });
      notification.success({
        message: 'Ordem do pedigo gerada!',
      });
    },
    onError() {
      notification.error({
        message: 'Não foi possível gerar a ordem do pedido!',
      });
    },
  });
}

export function usePecaAiBasketRemoveItem(
  params: Omit<PecaAiBasketRemoveItemRequest, 'productId'>,
) {
  const queryChanel = useQueryChanel();
  const syncBasketInWorkorder = useWorkorderComplementSyncWithPecaAi();

  return useMutation(
    (productId: string) =>
      pecaAiBasketServices.removeItemBasket({ ...params, productId }),
    {
      async onSuccess() {
        if (params.complementId && params.workOrderId) {
          await syncBasketInWorkorder.mutateAsync({
            complementId: params.complementId,
            workOrderId: params.workOrderId,
          });
        }

        queryChanel({
          invalidateQueries: [
            [PECAAI_BASKET_LIST],
            [PECAAI_BASKET_DETAIL],
            [PECAAI_BASKET_SHIPPINGS],
            ['workOrders'],
            [WORKORDER_COMPLEMENT_DETAIL_KEY],
            [WORKORDER_ITEMS_QUERY_KEY],
          ],
          removeQueries: [[WORKORDER_COMPLEMENT_DETAIL_KEY]],
        });

        notification.success({
          message: 'Produto removido com sucesso!',
        });
      },
      onError() {
        notification.error({
          message: 'Não foi possível remover o produto!',
        });
      },
    },
  );
}

export function usePecaAiBasketEditItem(
  params: Omit<PecaAiBasketEditItemRequest, 'productId' | 'amount'>,
) {
  const queryChanel = useQueryChanel();
  const syncBasketInWorkorder = useWorkorderComplementSyncWithPecaAi();

  return useMutation(
    (vars: Omit<PecaAiBasketEditItemRequest, 'basketId' | 'buyerId'>) =>
      pecaAiBasketServices.editItemBasket({ ...params, ...vars }),
    {
      async onSuccess() {
        if (params.complementId && params.workOrderId) {
          await syncBasketInWorkorder.mutateAsync({
            complementId: params.complementId,
            workOrderId: params.workOrderId,
          });
        }

        queryChanel({
          invalidateQueries: [
            [PECAAI_BASKET_LIST],
            [PECAAI_BASKET_DETAIL],
            [PECAAI_BASKET_SHIPPINGS],
            ['workOrders'],
            [WORKORDER_COMPLEMENT_DETAIL_KEY],
            [WORKORDER_ITEMS_QUERY_KEY],
          ],
          removeQueries: [[WORKORDER_COMPLEMENT_DETAIL_KEY]],
        });

        notification.success({
          message: 'Produto editado com sucesso!',
        });
      },
      onError() {
        notification.error({
          message: 'Não foi possível editar o produto!',
        });
      },
    },
  );
}

export function usePecaAiAddProductInBasket() {
  return useMutation({
    mutationFn: pecaAiBasketServices.addProductInBasket,
    onSuccess() {
      notification.success({ message: 'Produto adicionado a cesta' });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePecaAiRemoveProductInBasket() {
  return useMutation({
    mutationFn: pecaAiBasketServices.removeProductInBasket,
    onSuccess() {
      notification.success({ message: 'Produto removido a cesta' });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function usePecaAiGenerateOrder() {
  const queryChanel = useQueryChanel();

  return useMutation({
    mutationFn: pecaAiBasketServices.generateOrder,
    onSuccess() {
      notification.success({ message: 'Pedido de compra enviado com sucesso' });

      queryChanel({ invalidateQueries: [[PECAAI_BASKET_DETAIL]] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
